import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import "./HelpPage.css";

const FAQS_TROUBLESHOOT = [
  {
    q: "Can't see challenges or battles?",
    a: "Make sure you're logged in and your email is verified. If the arena still looks empty, try a hard refresh (Ctrl+Shift+R). Privacy extensions or VPNs can block loading scripts — disable them temporarily and retry.",
  },
  {
    q: "My submission isn't getting scored",
    a: "This usually happens if your connection drops mid-submit. Check your internet and hit Re-submit. If it persists, open the browser console (F12 → Console), note any API errors, and send them to support with your Match ID.",
  },
  {
    q: "AI judge stuck on 'Scoring…'?",
    a: "The judge runs real-time AI analysis — on slow networks it can take up to 30 seconds. Try refreshing. Ad-blockers sometimes block the AI response endpoint; disable them and resubmit.",
  },
  {
    q: "ELO or leaderboard not updating?",
    a: "Leaderboards refresh every 10 minutes. If your latest score isn't showing, wait and refresh. Make sure your submission returned a 'Submitted ✅' confirmation — guest mode doesn't track ELO.",
  },
  {
    q: "Progress or badges not saving?",
    a: "Ensure cookies and local storage are enabled — they're required for session tracking. If you switched devices, log in with the same account to pull your cloud data back in.",
  },
  {
    q: "Didn't receive a verification or reset email?",
    a: "Check your spam or promotions folder and add promptiya.com to your safe-sender list. If it still doesn't arrive, request a resend from the login screen.",
  },
  {
    q: "Getting 'Access Denied' or 'Unauthorized' errors?",
    a: "Your session likely expired. Log out and back in to refresh your auth token. If you're running multiple accounts in the same browser, try a private window or clear cookies for promptiya.com.",
  },
  {
    q: "Still stuck?",
    a: "Email support@promptiya.com with a screenshot, your browser version, and the Match or Challenge ID. The more detail you give us, the faster we can fix it.",
  },
];

export default function ResourcesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    if (location.hash === "#video-section") {
      setTimeout(() => {
        const el = document.getElementById("video-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location]);

  return (
    <div className="resources-page">

      {/* ── Hero ── */}
      <section className="hero-section-help">
        <div className="container-help">
          <div className="hero-content-help">
            <div className="badge-help">Resources & Support</div>
            <h1 className="hero-title-help">
              Learn Prompt Engineering by{" "}
              <span className="gradient-text-help">Battling for It</span>
            </h1>
            <p className="hero-subtitle-help">
              Prompttar puts you in head-to-head battles against real opponents.
              Same challenge, same clock — highest AI score wins. Every match
              makes you a sharper prompt engineer.
            </p>
            <button className="cta-button primary-help" onClick={() => navigate("/tasks")}>
              <span>Enter the Arena</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 17l9.2-9.2M17 17V7H7" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="features-section-help">
        <div className="container-help">
          <div className="section-header-help">
            <h2 className="section-title-help">Two Modes. One Goal.</h2>
            <p className="section-subtitle-help">
              Solo challenges to sharpen your skills. Head-to-head battles to prove them.
            </p>
          </div>

          <div className="features-grid-help">
            <div className="feature-card-help">
              <div className="feature-icon-help chrome-help">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3 className="feature-title-help">Solo Challenges</h3>
              <p className="feature-description-help">
                Practice across Beginner → Expert tiers at your own pace. Each challenge
                gives you a real-world scenario. Submit your prompt and get an instant
                AI score with a full breakdown and model answer to compare against.
              </p>
              <div className="feature-benefits-help">
                {[
                  "66 real-world professional scenarios",
                  "13 topic categories",
                  "Instant accuracy, efficiency & creativity scores",
                  "Model answers to learn from every time",
                ].map((b) => (
                  <div className="benefit-item-help" key={b}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="feature-card-help">
              <div className="feature-icon-help ai-help">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" stroke="currentColor" strokeWidth="2" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="currentColor" strokeWidth="2" />
                  <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="2" />
                </svg>
              </div>
              <h3 className="feature-title-help">Head-to-Head Ranked Battles</h3>
              <p className="feature-description-help">
                Challenge any player worldwide — same task, same clock. The AI judge
                scores both submissions instantly. Higher score wins, updates your ELO,
                and moves you up (or down) the World Leaderboard.
              </p>
              <div className="feature-benefits-help">
                {[
                  "Matched against opponents at your ELO level",
                  "2-min Blitz · 5-min Sprint · 10-min Deep Dive",
                  "AI judge scores in under 2 seconds",
                  "ELO rating updates after every match, win or lose",
                ].map((b) => (
                  <div className="benefit-item-help" key={b}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Step-by-step ── */}
      <section className="getting-started-section-help">
        <div className="container-help">
          <div className="section-header-help">
            <div className="section-badge-help">Quick Start</div>
            <h2 className="section-title-help">Sign-up to World #1 — Six Steps</h2>
            <p className="section-subtitle-help">First match in under two minutes.</p>
          </div>

          <div className="steps-container-help">
            {[
              { n: "01", title: "Log in & pick a mode", desc: "Create your account and choose Solo Challenge or Ranked Battle." },
              { n: "02", title: "Choose your format", desc: "Blitz (2 min), Sprint (5 min), or Deep Dive (10 min)." },
              { n: "03", title: "Read the brief", desc: "Task, context, and scoring criteria. Write your sharpest prompt before zero." },
              { n: "04", title: "Submit", desc: "AI scores Accuracy (40%), Efficiency (25%), Creativity (20%), Alignment (15%)." },
              { n: "05", title: "Review the breakdown", desc: "Your score vs opponent's, prompts side by side, plus the model answer." },
              { n: "06", title: "Climb", desc: "Wins boost ELO. Track your division: Bronze → Silver → Gold → Platinum → Diamond → Grandmaster." },
            ].map((step, i, arr) => (
              <React.Fragment key={step.n}>
                <div className="step-item-help">
                  <div className="step-number-help">{step.n}</div>
                  <div className="step-content-help">
                    <h3 className="step-title-help">{step.title}</h3>
                    <p className="step-description-help">{step.desc}</p>
                  </div>
                </div>
                {i < arr.length - 1 && <div className="step-connector-help" />}
              </React.Fragment>
            ))}
          </div>
          <p className="arena-steps-footer" style={{ marginTop: "2.5rem" }}>
            ⚔️ Practice. Battle. Rank. Repeat — one prompt at a time.
          </p>
        </div>
      </section>

      {/* ── Scoring ── */}
      <section className="features-section-help">
        <div className="container-help">
          <div className="section-header-help">
            <div className="section-badge-help">Scoring</div>
            <h2 className="section-title-help">How Your Prompt Gets Judged</h2>
            <p className="section-subtitle-help">
              Every submission is scored across four dimensions by the AI judge in real time.
            </p>
          </div>

          <div className="features-grid-help" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
            {[
              { pct: "40%", label: "Accuracy", desc: "Does the prompt solve the task correctly and completely?" },
              { pct: "25%", label: "Efficiency", desc: "Is it concise without sacrificing clarity?" },
              { pct: "20%", label: "Creativity", desc: "Does it use smart framing, constraints, or examples?" },
              { pct: "15%", label: "AI Alignment", desc: "How precisely does it guide the model's behaviour?" },
            ].map((s) => (
              <div className="feature-card-help" key={s.label} style={{ textAlign: "center" }}>
                <div className="scoring-pct">{s.pct}</div>
                <h3 className="feature-title-help" style={{ marginBottom: "6px" }}>{s.label}</h3>
                <p className="feature-description-help" style={{ fontSize: "13px", marginBottom: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>

          <p style={{ textAlign: "center", color: "#9ca3af", fontSize: "13px", marginTop: "2rem" }}>
            Battle wins also boost your <strong style={{ color: "#6b7280" }}>ELO rating</strong> beyond the base score.
            Monthly resets move everyone to the nearest lower division — keeping competition fresh every cycle.
          </p>
        </div>
      </section>

      {/* ── Video ── */}
      <section className="video-section-help" id="video-section">
        <div className="container-help">
          <div className="video-content-help">
            <div className="video-info-help">
              <div className="section-badge-help">See It Live</div>
              <h2 className="section-title-help">Watch a Ranked Battle</h2>
              <p className="section-subtitle-help">
                A real 5-minute Sprint — from challenge reveal to AI judging and ELO update.
              </p>
              <a href="/tasks" className="docs-link">
                <span>Try a Solo Challenge</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 17l9.2-9.2M17 17V7H7" />
                </svg>
              </a>
            </div>
            <div className="video-wrapper-help">
              <div className="video-container-help">
                <video controls width="100%">
                  <source
                    src="https://res.cloudinary.com/dmvjrgoqa/video/upload/v1760774296/PromptDrills_Demo_pd2sgh.mp4"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Divisions ── */}
      <section className="getting-started-section-help">
        <div className="container-help">
          <div className="section-header-help">
            <div className="section-badge-help">Divisions & ELO</div>
            <h2 className="section-title-help">The Ranking Ladder</h2>
            <p className="section-subtitle-help">
              Your ELO determines your division. Win battles to climb. Monthly resets keep it honest.
            </p>
          </div>

          <div className="features-grid-help" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}>
            {[
              { icon: "🥉", label: "Bronze",      desc: "Starting tier — build your fundamentals." },
              { icon: "🥈", label: "Silver",      desc: "Consistent prompts, rising win rate." },
              { icon: "🥇", label: "Gold",        desc: "Sharp, concise prompts under pressure." },
              { icon: "💎", label: "Platinum",    desc: "High creativity and efficiency scores." },
              { icon: "💠", label: "Diamond",     desc: "Elite — top percentile globally." },
              { icon: "👑", label: "Grandmaster", desc: "World-class prompt engineering." },
            ].map((d) => (
              <div className="feature-card-help" key={d.label} style={{ textAlign: "center" }}>
                <span className="division-emoji">{d.icon}</span>
                <h3 className="feature-title-help" style={{ marginBottom: "6px" }}>{d.label}</h3>
                <p className="feature-description-help" style={{ fontSize: "12px", marginBottom: 0 }}>{d.desc}</p>
              </div>
            ))}
          </div>

          <p style={{ textAlign: "center", color: "#9ca3af", fontSize: "13px", marginTop: "1.5rem" }}>
            Everyone drops to the nearest lower division on reset. No one coasts to the top.
          </p>
        </div>
      </section>

      {/* ── Troubleshooting ── */}
      <section className="troubleshooting-section-help">
        <div className="container-help">
          <div className="section-header-help">
            <h2 className="section-title-help">Quick Troubleshooting</h2>
            <p className="section-subtitle-help">
              Common issues, real fixes — get back in the arena fast.
            </p>
          </div>

          <div className="faq-grid-help">
            {FAQS_TROUBLESHOOT.map((faq, i) => (
              <div
                key={i}
                className={`faq-item-help ${openFaq === i ? "arena-faq-item--open" : ""}`}
              >
                <button
                  className="faq-question-help"
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    textAlign: "left",
                    gap: "12px",
                  }}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <h3 style={{ margin: 0 }}>{faq.q}</h3>
                  <span style={{ fontSize: "18px", color: "#9ca3af", flexShrink: 0 }}>
                    {openFaq === i ? "−" : "+"}
                  </span>
                </button>
                {openFaq === i && <p className="faq-answer-help">{faq.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="arena-cta" style={{ textAlign: "center", padding: "80px 24px" }}>
        <div className="container-help">
          <h2>Ready to Enter the Arena?</h2>
          <p style={{ maxWidth: "480px", margin: "12px auto 32px" }}>
            Thousands of prompt engineers are competing, learning, and climbing the World
            Leaderboard right now. No credit card required.
          </p>
          <button className="cta-button primary-help" onClick={() => navigate("/tasks")}>
            <span>Start Free — Battle Now</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
          </button>
        </div>
      </section>

    </div>
  );
}